import { useState } from 'react';
import { colors } from '../styles/colors';

// Must match Expo app's salt for hash compatibility
const PIN_SALT = 'OurHertz_PIN_Salt_2024';

/** SHA-256 hash with salt (compatible with Expo's CryptoJS.SHA256(pin + salt)) */
async function hashPin(pin: string): Promise<string> {
  const data = new TextEncoder().encode(pin + PIN_SALT);
  const buf = await crypto.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

/** Verify PIN against a stored hash */
export async function verifyPinHash(pin: string, storedHash: string): Promise<boolean> {
  const hash = await hashPin(pin);
  return hash === storedHash;
}

/** Save PIN to Firestore (called from settings) */
export async function savePinToFirestore(pin: string): Promise<void> {
  const hash = await hashPin(pin);
  const { useAuthStore } = await import('../stores/authStore');
  const uid = useAuthStore.getState().user?.uid;
  if (!uid) return;
  const { doc, updateDoc } = await import('firebase/firestore');
  const { db } = await import('../lib/firebase/app');
  await updateDoc(doc(db, 'users', uid), { pinEnabled: true, pinHash: hash });
}

/** Disable PIN in Firestore */
export async function disablePinInFirestore(): Promise<void> {
  const { useAuthStore } = await import('../stores/authStore');
  const uid = useAuthStore.getState().user?.uid;
  if (!uid) return;
  const { doc, updateDoc } = await import('firebase/firestore');
  const { db } = await import('../lib/firebase/app');
  await updateDoc(doc(db, 'users', uid), { pinEnabled: false, pinHash: '' });
}

// Shared keypad component
function PinKeypad({ title, subtitle, onComplete, onCancel }: {
  title: string;
  subtitle?: string;
  onComplete: (pin: string) => void;
  onCancel?: () => void;
}) {
  const [pin, setPin] = useState('');

  const handleKey = (n: number | 'del') => {
    if (n === 'del') { setPin(p => p.slice(0, -1)); return; }
    const next = pin + n;
    setPin(next);
    if (next.length === 4) {
      setTimeout(() => { onComplete(next); setPin(''); }, 150);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <h2 className="mb-1 text-lg font-bold" style={{ color: colors.text }}>{title}</h2>
      {subtitle && <p className="mb-4 text-xs" style={{ color: colors.textMuted }}>{subtitle}</p>}

      <div className="mb-6 flex gap-3">
        {[0,1,2,3].map(i => (
          <div key={i} className="h-3.5 w-3.5 rounded-full transition-all"
            style={{ backgroundColor: i < pin.length ? colors.primary : 'transparent', border: `2px solid ${i < pin.length ? colors.primary : colors.border}` }} />
        ))}
      </div>

      <div className="grid grid-cols-3 gap-2.5">
        {[1,2,3,4,5,6,7,8,9,null,0,'del'].map((n, i) => (
          <button key={i} onClick={() => { if (n !== null) handleKey(n as any); }}
            className="flex h-14 w-14 items-center justify-center rounded-full text-xl font-bold transition-colors active:opacity-70"
            style={{ backgroundColor: n === null ? 'transparent' : colors.card, color: colors.text, border: n === null ? 'none' : `1px solid ${colors.border}` }}>
            {n === 'del' ? '⌫' : n === null ? '' : n}
          </button>
        ))}
      </div>

      {onCancel && (
        <button onClick={onCancel} className="mt-4 text-sm" style={{ color: colors.textMuted }}>Cancel</button>
      )}
    </div>
  );
}

// PIN lock screen — requires pinHash prop from Firestore user data
interface LockProps {
  pinHash: string;
  onUnlock: () => void;
}

export default function PinLock({ pinHash, onUnlock }: LockProps) {
  const [error, setError] = useState('');
  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center" style={{ backgroundColor: colors.background }}>
      <h1 className="mb-6 text-2xl font-bold" style={{ color: colors.primary }}>OurHertz</h1>
      <PinKeypad title="PIN" onComplete={async (pin) => {
        if (await verifyPinHash(pin, pinHash)) { setError(''); onUnlock(); }
        else setError('Incorrect PIN');
      }} />
      {error && <p className="mt-2 text-xs" style={{ color: colors.error }}>{error}</p>}
    </div>
  );
}

// Settings PIN modal: setup (2x confirm) or disable (verify current)
type PinSettingsMode = 'setup' | 'disable';

export function PinSettingsModal({ mode, pinHash, onDone }: { mode: PinSettingsMode; pinHash?: string; onDone: (success: boolean) => void }) {
  const [step, setStep] = useState<'first' | 'confirm' | 'verify'>(() => mode === 'setup' ? 'first' : 'verify');
  const [firstPin, setFirstPin] = useState('');
  const [error, setError] = useState('');
  const isKo = document.documentElement.lang === 'ko' || localStorage.getItem('ourhertz_language') === '"ko"';

  return (
    <div className="fixed inset-0 z-[100] flex flex-col items-center justify-center" style={{ backgroundColor: colors.background }}>
      {step === 'first' && (
        <PinKeypad
          title={isKo ? 'PIN 설정' : 'Set PIN'}
          subtitle={isKo ? '4자리 PIN을 입력하세요' : 'Enter 4-digit PIN'}
          onComplete={(pin) => { setFirstPin(pin); setStep('confirm'); }}
          onCancel={() => onDone(false)}
        />
      )}
      {step === 'confirm' && (
        <>
          <PinKeypad
            title={isKo ? 'PIN 확인' : 'Confirm PIN'}
            subtitle={isKo ? '같은 PIN을 다시 입력하세요' : 'Re-enter the same PIN'}
            onComplete={async (pin) => {
              if (pin === firstPin) { await savePinToFirestore(pin); onDone(true); }
              else { setError(isKo ? 'PIN이 일치하지 않습니다. 다시 시도하세요.' : 'PINs do not match. Try again.'); setStep('first'); setFirstPin(''); }
            }}
            onCancel={() => onDone(false)}
          />
          {error && <p className="mt-2 text-xs" style={{ color: colors.error }}>{error}</p>}
        </>
      )}
      {step === 'verify' && (
        <>
          <PinKeypad
            title={isKo ? 'PIN 해제' : 'Remove PIN'}
            subtitle={isKo ? '현재 PIN을 입력하세요' : 'Enter current PIN'}
            onComplete={async (pin) => {
              if (pinHash && await verifyPinHash(pin, pinHash)) { await disablePinInFirestore(); onDone(true); }
              else { setError(isKo ? 'PIN이 일치하지 않습니다.' : 'Incorrect PIN.'); }
            }}
            onCancel={() => onDone(false)}
          />
          {error && <p className="mt-2 text-xs" style={{ color: colors.error }}>{error}</p>}
        </>
      )}
    </div>
  );
}
